<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  notification: {
    type: Object,
    required: true
  },
  compact: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['read', 'delete'])

const { t } = useI18n()

const typeIcon = computed(() => {
  switch (props.notification.type) {
    case 'meal_reminder': return '🍽️'
    case 'shopping_reminder': return '🛒'
    case 'recipe_suggestion': return '📖'
    case 'system': return '⚙️'
    default: return '🔔'
  }
})

const typeLabel = computed(() => {
  const types = {
    'meal_reminder': t('notifications.meal_reminder'),
    'shopping_reminder': t('notifications.shopping_reminder'),
    'recipe_suggestion': t('notifications.recipe_suggestion'),
    'system': t('notifications.system')
  }
  return types[props.notification.type] || t('notifications.system')
})

const timeAgo = computed(() => {
  const date = new Date(props.notification.created_at)
  const now = new Date()
  const diff = Math.floor((now - date) / 1000)
  
  if (diff < 60) return t('notifications.justNow')
  if (diff < 3600) return t('notifications.minutesAgo', { count: Math.floor(diff / 60) })
  if (diff < 86400) return t('notifications.hoursAgo', { count: Math.floor(diff / 3600) })
  if (diff < 604800) return t('notifications.daysAgo', { count: Math.floor(diff / 86400) })
  
  return date.toLocaleDateString()
})

function handleMarkAsRead() {
  if (!props.notification.is_read) {
    emit('read', props.notification.id)
  }
}

function handleDelete() {
  emit('delete', props.notification.id)
}
</script>

<template>
  <div 
    :class="[
      'notification-item',
      { 
        'unread': !notification.is_read,
        'compact': compact
      }
    ]"
    @click="handleMarkAsRead"
  >
    <div class="notification-icon">
      {{ typeIcon }}
    </div>
    
    <div class="notification-content">
      <div class="notification-header">
        <span class="notification-type">{{ typeLabel }}</span>
        <span class="notification-time">{{ timeAgo }}</span>
      </div>
      
      <h4 v-if="notification.title && !compact" class="notification-title">
        {{ notification.title }}
      </h4>
      
      <p class="notification-message">
        {{ notification.message }}
      </p>
    </div>
    
    <div class="notification-actions">
      <button 
        v-if="!notification.is_read"
        class="action-btn read-btn"
        :title="t('notifications.markAsRead')"
        @click.stop="handleMarkAsRead"
      >
        ✓
      </button>
      <button 
        class="action-btn delete-btn"
        :title="t('app.delete')"
        @click.stop="handleDelete"
      >
        ✕
      </button>
    </div>
    
    <div v-if="!notification.is_read" class="unread-indicator" />
  </div>
</template>

<style scoped>
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--color-background);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
  position: relative;
}

.notification-item:hover {
  background: var(--color-background-secondary);
}

.notification-item.unread {
  background: var(--color-primary-light);
}

.notification-item.unread:hover {
  background: var(--color-primary-lighter);
}

.notification-item.compact {
  padding: 10px 12px;
}

.notification-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-background-secondary);
  border-radius: 10px;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.compact .notification-icon {
  width: 32px;
  height: 32px;
  font-size: 1rem;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.notification-type {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.notification-time {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.notification-title {
  margin: 0 0 4px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-text);
}

.notification-message {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.compact .notification-message {
  -webkit-line-clamp: 1;
}

.notification-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.notification-item:hover .notification-actions {
  opacity: 1;
}

.action-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  transition: background 0.2s, color 0.2s;
}

.action-btn:hover {
  background: var(--color-background-secondary);
}

.read-btn:hover {
  color: var(--color-primary);
}

.delete-btn:hover {
  color: #f44336;
}

.unread-indicator {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: var(--color-primary);
  border-radius: 50%;
}

.notification-item:hover .unread-indicator {
  display: none;
}

@media (max-width: 480px) {
  .notification-actions {
    opacity: 1;
  }
  
  .unread-indicator {
    display: none;
  }
}
</style>
